import _ from 'lodash'

const name = 'responsiveSiteApiExtension'

const getContainerMeasures = (windowObject, node) => {
    if (!node) {
        return {}
    }
    const {
        gridTemplateRows,
        gridTemplateColumns,
        gridRowGap,
        gridColumnGap
    } = windowObject.getComputedStyle(node)
    return {
        gridTemplateRows,
        gridTemplateColumns,
        gridRowGap,
        gridColumnGap
    }
}

const isMinusZero = value => 1 / value === -Infinity

const roundToNearestHalf = num => {
    const nearestHalf = Math.round(num * 2) / 2

    return isMinusZero(nearestHalf) ? 0 : nearestHalf
}

const getRotationInRadians = (windowObject, node) => {
    const transform = windowObject.getComputedStyle(node).transform

    return transform === 'none' ?
        null :
        Math.asin(transform.split('(')[1].split(')')[0].split(',')[1])
}

const getCompMeasuredRelativeToViewportBoundingBox = (windowObject, node) => {
    const boundingClientRect = node.getBoundingClientRect()

    return {
        height: boundingClientRect.height,
        width: boundingClientRect.width,
        absoluteTop: boundingClientRect.top,
        absoluteLeft: boundingClientRect.left
    }
}

const toNonRotated = (windowObject, node, boundingBox) => {
    const rotationInRad = getRotationInRadians(windowObject, node)

    if (!rotationInRad) {
        return boundingBox
    }

    const width = node.offsetWidth
    const height = node.offsetHeight
    const absoluteTop = roundToNearestHalf(boundingBox.absoluteTop + (boundingBox.height - height) / 2)
    const absoluteLeft = roundToNearestHalf(boundingBox.absoluteLeft + (boundingBox.width - width) / 2)

    return {
        absoluteTop,
        absoluteLeft,
        width,
        height
    }
}

const getResponsivePrivateApis = siteApiData => {
    const {
        getContainerClasses,
        windowObject,
        endDetach,
        startDetach,
        getDetachedItemsIds,
        setDetachedItemBoundingBox,
        setDetachedItemRotation
    } = siteApiData.responsive

    const containerClasses = getContainerClasses()

    const getLayoutContainer = containerId => {
        const containerNode = windowObject.document.getElementById(containerId)

        if (!containerNode || !containerClasses[containerId]) {
            return null
        }

        return containerNode.querySelector(`.${containerClasses[containerId].layoutContainerClassName}`)
    }

    const getOverflowWrapper = containerId => {
        const containerNode = windowObject.document.getElementById(containerId)

        if (!containerNode || !containerClasses[containerId]) {
            return null
        }

        return containerNode.querySelector(`.${containerClasses[containerId].layoutContainerOverflowWrapperClassName}`)
    }

    const getGridContainerMeasures = compId =>
        getContainerMeasures(windowObject, getLayoutContainer(compId))

    const getGridMeasures = compId => getGridContainerMeasures(compId)

    const getScrollHeight = compId => {
        const contentWrapper = getOverflowWrapper(compId) || getLayoutContainer(compId)

        return contentWrapper ? contentWrapper.scrollHeight : null
    }

    const getClientHeight = compId => {
        const contentWrapper = getOverflowWrapper(compId) || getLayoutContainer(compId)

        return contentWrapper ? contentWrapper.clientHeight : null
    }

    const getScrollWidth = compId => {
        const contentWrapper = getOverflowWrapper(compId) || getLayoutContainer(compId)

        return contentWrapper ? contentWrapper.scrollWidth : null
    }

    const getClientWidth = compId => {
        const contentWrapper = getOverflowWrapper(compId) || getLayoutContainer(compId)

        return contentWrapper ? contentWrapper.clientWidth : null
    }

    const detachMulti = compIds => {
        if (!_.isEmpty(getDetachedItemsIds())) {
            throw new Error('Already have detached comps')
        }

        const compsDetachData = _.reduce(compIds, (result, compId) => {
            const node = windowObject.document.getElementById(compId)
            const nonRotatedClientBoundingBox = node ? toNonRotated(windowObject, node, getCompMeasuredRelativeToViewportBoundingBox(windowObject, node)) : {}
            const boundingBox = {
                left: nonRotatedClientBoundingBox.absoluteLeft,
                top: nonRotatedClientBoundingBox.absoluteTop,
                width: nonRotatedClientBoundingBox.width,
                height: nonRotatedClientBoundingBox.height
            }

            result[compId] = {
                boundingBox
            }

            return result
        }, {})

        startDetach(compsDetachData)
    }

    const detachSingle = compId => detachMulti([compId])

    const updateDetached = (compId, boundingBox) => {
        if (_.has(getDetachedItemsIds(), compId)) {
            return setDetachedItemBoundingBox(compId, boundingBox)
        }

        throw new Error('Component must be detached before updating layout')
    }

    const updateDetachedRotation = (compId, rotationInDegrees) => {
        if (_.has(getDetachedItemsIds(), compId)) {
            return setDetachedItemRotation(compId, rotationInDegrees)
        }

        throw new Error('Component must be detached before updating rotation')
    }

    const clearDetached = () => endDetach()

    return {
        responsive: {
            getScrollHeight,
            getClientHeight,
            getScrollWidth,
            getClientWidth,
            getGridMeasures,
            detach: detachSingle,
            detachMulti,
            updateDetached,
            updateDetachedRotation,
            clearDetached
        }
    }
}

const functionLibrary = {
    getResponsivePrivateApis
}

export {name, functionLibrary}
